import { defineStore } from 'pinia'
import Network from '@/Network'
import { GroupResponse, ListGroupsResponse } from '@/Network/Types/Responses/GroupResponses'
import { CreateGroupRequest } from '@/Network/Types/Requests/GroupRequests'

export type GroupState = {
  groups: ListGroupsResponse | undefined
  filteredGroups: GroupResponse[]
  isLoading: boolean
  currentPage: number
  totalCount: number
}

export type Filter = {
  searchTerm?: string
}

export const useGroupStore = defineStore('group', {
  state: (): GroupState => ({
    groups: undefined,
    filteredGroups: [],
    isLoading: false,
    currentPage: 1,
    totalCount: 0
  }),

  actions: {
    async fetchByCompanies(ids: number[], page: number = 1, limit: number = 20) {
      if (this.isLoading) return
      try {
        this.isLoading = true
        const response = await Network.group.listByCompanies(ids, page, limit)
        const newGroups = response.data || []
        if (page === 1) {
          this.filteredGroups = [...newGroups]
        } else {
          const existingIds = new Set(this.filteredGroups.map((group) => group.id))
          const uniqueNewGroups = newGroups.filter((group) => !existingIds.has(group.id))
          if (uniqueNewGroups.length > 0) {
            this.filteredGroups = [...this.filteredGroups, ...uniqueNewGroups]
          }
        }

        this.totalCount = response.count
        this.currentPage = page

        if (this.groups) {
          this.groups.data = this.filteredGroups
        }
      } catch (error) {
        console.error('Erro ao buscar grupos:', error)
      } finally {
        this.isLoading = false
      }
    },

    async getAllGroups(): Promise<void> {
      try {
        this.isLoading = true
        const response = await Network.group.list()
        this.groups = response
        this.filteredGroups = response.data
        this.totalCount = response.count
      } finally {
        this.isLoading = false
      }
    },

    async create(payload: CreateGroupRequest) {
      await Network.group.create(payload)
    },

    async remove(groupId: number): Promise<void> {
      await Network.group.remove(groupId)
    },

    async update({
      groupId,
      payload
    }: {
      groupId: number
      payload: CreateGroupRequest
    }): Promise<void> {
      await Network.group.update(groupId, payload)
    },

    applyFilters(filters: Filter) {
      const { searchTerm } = filters
      if (!searchTerm) {
        this.filteredGroups = this.groups?.data || []
        return
      }

      const lowerCaseSearchTerm = searchTerm.toLowerCase()
      this.filteredGroups = this.filteredGroups.filter((group) =>
        group.name.toLowerCase().includes(lowerCaseSearchTerm)
      )
    }
  }
})
